import { sanitizeId } from "./textDataUtils";

/**
 * Saves canvas elements to IndexedDB with proper error handling and retries
 * @param {Object} params - Parameters object
 * @param {Object} params.data - Canvas data to save
 * @param {string} params.pageId - Page ID for storing data
 * @param {string} [params.storeName="canvasElements"] - IndexedDB store name
 */
export const saveCanvasElementsToIndexedDB = async ({
  data,
  pageId,
  storeName = "canvasElements",
}) => {
  const DB_NAME = "canvasElementsDB";
  const STORE_NAME = storeName;
  const DEFAULT_TIMEOUT = 5000;
  const MAX_RETRIES = 5;

  // Sanitize the pageId to ensure it's a valid IndexedDB key
  const sanitizeIdLocal = (id) =>
    sanitizeId(id, String(pageId || "").trim() || "unknown_page");

  const sanitizedPageId = sanitizeIdLocal(pageId);

  const getCurrentDBVersion = () =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const version = db.version;
        db.close();
        resolve(version);
      };
      request.onerror = () => reject(1); // fallback
    });

  const openAndSave = (version, retries = 0) =>
    new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("⚠️ IndexedDB open request timed out."));
      }, DEFAULT_TIMEOUT);

      const request = indexedDB.open(DB_NAME, version);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };

      request.onsuccess = (event) => {
        clearTimeout(timeoutId);
        const db = event.target.result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.close();
          if (retries < MAX_RETRIES) {
            return openAndSave(version + 1, retries + 1)
              .then(resolve)
              .catch(reject);
          } else {
            return reject(
              new Error("❌ Max retries reached creating object store.")
            );
          }
        }

        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);

        // Recursively remove all functions from an object to make it IndexedDB-compatible
        const removeFunctions = (obj, visited = new WeakSet()) => {
          // Handle null, undefined, or primitives
          if (obj === null || obj === undefined || typeof obj !== "object") {
            return obj;
          }

          // Handle circular references
          if (visited.has(obj)) {
            return null;
          }

          // Handle special objects that should be preserved (IndexedDB can serialize these)
          if (
            obj instanceof Date ||
            obj instanceof Blob ||
            obj instanceof File ||
            obj instanceof ArrayBuffer ||
            obj instanceof RegExp ||
            obj instanceof Int8Array ||
            obj instanceof Uint8Array ||
            obj instanceof Uint8ClampedArray ||
            obj instanceof Int16Array ||
            obj instanceof Uint16Array ||
            obj instanceof Int32Array ||
            obj instanceof Uint32Array ||
            obj instanceof Float32Array ||
            obj instanceof Float64Array
          ) {
            return obj;
          }

          // Skip DOM elements, Konva nodes, and Event objects (these cannot be serialized)
          if (
            obj instanceof HTMLElement ||
            obj instanceof Node ||
            obj instanceof Event ||
            (obj.constructor && (obj.constructor.name === "Node" || obj.constructor.name === "KonvaEventObject"))
          ) {
            return null;
          }

          // Skip objects that look like Konva events (have evt, type, target properties)
          if (obj.evt && obj.type && (obj.target || obj.currentTarget)) {
            return null;
          }

          // Handle arrays
          if (Array.isArray(obj)) {
            visited.add(obj);
            return obj.map((item) => removeFunctions(item, visited));
          }

          // Handle plain objects
          visited.add(obj);
          const cleaned = {};

          for (const key in obj) {
            if (!obj.hasOwnProperty(key)) {
              continue;
            }

            const value = obj[key];

            // Skip functions (these cannot be serialized by IndexedDB)
            if (typeof value === "function") {
              continue;
            }

            // Skip DOM elements and Event objects (these cannot be serialized)
            if (value instanceof HTMLElement || value instanceof Node || value instanceof Event) {
              continue;
            }

            // Skip objects that look like Konva events
            if (value && typeof value === "object" && value.evt && value.type && (value.target || value.currentTarget)) {
              continue;
            }

            // Recursively clean nested objects/arrays
            if (typeof value === "object" && value !== null) {
              cleaned[key] = removeFunctions(value, visited);
            } else {
              // For primitives, keep as is
              cleaned[key] = value;
            }
          }

          return cleaned;
        };

        // Sanitize all element IDs as well
        const sanitizeElements = (elements) => {
          const sanitized = {};
          Object.keys(elements).forEach((key) => {
            if (Array.isArray(elements[key])) {
              sanitized[key] = elements[key].map((item) => {
                if (item && typeof item === "object" && item.id) {
                  // For images, handle HTMLImageElement conversion
                  if (key === "images") {
                    const sanitizedItem = {
                      ...item,
                      id: sanitizeId(item.id),
                    };

                    // Keep originalBlob if available (IndexedDB supports Blob storage natively)
                    if (item.originalBlob instanceof Blob) {
                      sanitizedItem.originalBlob = item.originalBlob;
                    }

                    // PERFORMANCE FIX: Only store the compressed image to save memory
                    // We can reconstruct the full image from the compressed version if needed
                    // This reduces IndexedDB storage by ~75% for images

                    // Convert compressedImage to base64 data URL if it's an HTMLImageElement
                    if (item.compressedImage instanceof HTMLImageElement) {
                      try {
                        const canvas = document.createElement("canvas");
                        canvas.width =
                          item.compressedImage.naturalWidth ||
                          item.compressedImage.width ||
                          100;
                        canvas.height =
                          item.compressedImage.naturalHeight ||
                          item.compressedImage.height ||
                          100;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(item.compressedImage, 0, 0);
                        // PERFORMANCE: Use JPEG with 0.7 quality for storage (smaller size)
                        sanitizedItem.compressedImageDataUrl = canvas.toDataURL("image/jpeg", 0.7);
                      } catch (e) {
                        console.error(
                          "Failed to convert compressedImage to data URL:",
                          e
                        );
                      }
                      delete sanitizedItem.compressedImage; // Remove HTMLImageElement
                    }

                    // PERFORMANCE FIX: Only store compressed version, not all copies
                    // Convert image to base64 ONLY if compressedImage doesn't exist
                    if (item.image instanceof HTMLImageElement && !sanitizedItem.compressedImageDataUrl) {
                      try {
                        const canvas = document.createElement("canvas");
                        canvas.width =
                          item.image.naturalWidth || item.image.width || 100;
                        canvas.height =
                          item.image.naturalHeight || item.image.height || 100;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(item.image, 0, 0);
                        // PERFORMANCE: Use JPEG with 0.7 quality for storage
                        sanitizedItem.imageDataUrl = canvas.toDataURL("image/jpeg", 0.7);
                      } catch (e) {
                        console.error(
                          "Failed to convert image to data URL:",
                          e
                        );
                      }
                      delete sanitizedItem.image; // Remove HTMLImageElement
                    } else {
                      // If we have compressed version, don't store the full image
                      delete sanitizedItem.image;
                      // Use compressed as the main image source
                      if (sanitizedItem.compressedImageDataUrl && !sanitizedItem.imageDataUrl) {
                        sanitizedItem.imageDataUrl = sanitizedItem.compressedImageDataUrl;
                      }
                    }

                    // PERFORMANCE FIX: Don't store originalImage separately - it's too large
                    // We can use the compressed version for display
                    if (item.originalImage instanceof HTMLImageElement) {
                      // Only store if we don't have any other image data
                      if (!sanitizedItem.imageDataUrl && !sanitizedItem.compressedImageDataUrl) {
                        try {
                          const canvas = document.createElement("canvas");
                          canvas.width =
                            item.originalImage.naturalWidth ||
                            item.originalImage.width ||
                            100;
                          canvas.height =
                            item.originalImage.naturalHeight ||
                            item.originalImage.height ||
                            100;
                          const ctx = canvas.getContext("2d");
                          ctx.drawImage(item.originalImage, 0, 0);
                          // PERFORMANCE: Use JPEG with 0.7 quality
                          sanitizedItem.originalImageDataUrl = canvas.toDataURL("image/jpeg", 0.7);
                        } catch (e) {
                          console.error(
                            "Failed to convert originalImage to data URL:",
                            e
                          );
                        }
                      }
                      delete sanitizedItem.originalImage; // Remove HTMLImageElement
                    }

                    // PERFORMANCE FIX: Don't store uncompressed cropped image - it's huge!
                    // We can reconstruct from compressed version if needed
                    if (item.uncompressedCroppedImage instanceof HTMLImageElement) {
                      // Don't store this at all - it's too large and not needed
                      delete sanitizedItem.uncompressedCroppedImage;
                      // Don't create uncompressedCroppedImageDataUrl
                    }

                    // Ensure cropData is preserved for cropped images
                    if (item.cropData) {
                      sanitizedItem.cropData = item.cropData;
                    }

                    // Preserve original dimensions for cropped images
                    if (item.originalWidth) {
                      sanitizedItem.originalWidth = item.originalWidth;
                    }
                    if (item.originalHeight) {
                      sanitizedItem.originalHeight = item.originalHeight;
                    }

                    // Preserve flip and rotation data
                    if (item.flipHorizontal !== undefined) {
                      sanitizedItem.flipHorizontal = item.flipHorizontal;
                    }
                    if (item.flipVertical !== undefined) {
                      sanitizedItem.flipVertical = item.flipVertical;
                    }
                    if (item.originalCropRotation !== undefined) {
                      sanitizedItem.originalCropRotation =
                        item.originalCropRotation;
                    }
                    if (item.hasBakedTransforms !== undefined) {
                      sanitizedItem.hasBakedTransforms =
                        item.hasBakedTransforms;
                    }
                    if (item.rotation !== undefined) {
                      sanitizedItem.rotation = item.rotation;
                    }
                    if (item.scaleX !== undefined) {
                      sanitizedItem.scaleX = item.scaleX;
                    }
                    if (item.scaleY !== undefined) {
                      sanitizedItem.scaleY = item.scaleY;
                    }

                    // Convert compressedImage to base64 data URL if it's an HTMLImageElement
                    if (item.compressedImage instanceof HTMLImageElement) {
                      try {
                        const canvas = document.createElement("canvas");
                        canvas.width =
                          item.compressedImage.naturalWidth ||
                          item.compressedImage.width ||
                          100;
                        canvas.height =
                          item.compressedImage.naturalHeight ||
                          item.compressedImage.height ||
                          100;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(item.compressedImage, 0, 0);
                        sanitizedItem.compressedImageDataUrl =
                          canvas.toDataURL();
                      } catch (e) {
                        console.error(
                          "Failed to convert compressedImage to data URL:",
                          e
                        );
                      }
                      delete sanitizedItem.compressedImage; // Remove HTMLImageElement
                    }

                    // Remove all functions from the sanitized item
                    return removeFunctions(sanitizedItem);
                  }

                  // For other element types, sanitize ID and remove functions
                  const itemWithSanitizedId = { ...item, id: sanitizeId(item.id) };
                  return removeFunctions(itemWithSanitizedId);
                }
                // For non-object items, just remove functions if it's an object
                if (item && typeof item === "object") {
                  return removeFunctions(item);
                }
                return item;
              });
            } else {
              // For non-array values, remove functions if it's an object
              if (elements[key] && typeof elements[key] === "object") {
                sanitized[key] = removeFunctions(elements[key]);
              } else {
                sanitized[key] = elements[key];
              }
            }
          });
          return sanitized;
        };

        const sanitizedElements = sanitizeElements(data);

        // Final safety check: ensure the entire data structure is function-free
        const finalSanitizedData = removeFunctions(sanitizedElements);

        // Preserve validTextShapeIds from existing record when incoming save omits it (e.g. MyCanvas save after refresh)
        const preserveValidTextShapeIds = (existingResult, dataOut) => {
          const hasIncoming = Array.isArray(dataOut.validTextShapeIds) && dataOut.validTextShapeIds.length > 0;
          if (hasIncoming) return;
          const existing = existingResult?.data?.validTextShapeIds;
          if (Array.isArray(existing) && existing.length > 0) {
            dataOut.validTextShapeIds = existing;
          }
        };

        const dataToSave = {
          id: sanitizedPageId,
          data: finalSanitizedData,
          lastModified: new Date().toISOString(),
        };

        // Validate the data structure before saving
        // Check if any element IDs are invalid
        const validateElementIds = (data) => {
          const issues = [];
          Object.keys(data).forEach((key) => {
            if (Array.isArray(data[key])) {
              data[key].forEach((item, index) => {
                if (item && typeof item === "object") {
                  if (!item.id) {
                    issues.push(`${key}[${index}]: missing id`);
                  } else if (typeof item.id !== "string") {
                    issues.push(
                      `${key}[${index}]: id is not string (${typeof item.id})`
                    );
                  } else if (
                    item.id.includes("undefined") ||
                    item.id.includes("null")
                  ) {
                    issues.push(
                      `${key}[${index}]: id contains invalid value (${item.id})`
                    );
                  }
                }
              });
            }
          });
          return issues;
        };

        const validationIssues = validateElementIds(sanitizedElements);
        if (validationIssues.length > 0) {
          reject(
            new Error(`Data validation failed: ${validationIssues.join(", ")}`)
          );
          return;
        }

        // First, try to save a minimal test object to isolate the issue

        const testObject = {
          id: sanitizedPageId,
          data: { test: "data" },
          lastModified: new Date().toISOString(),
        };

        const testRequest = store.put(testObject);

        testRequest.onerror = (e) => {
          reject(new Error(`Test put failed: ${e.target.error?.message}`));
        };

        testRequest.onsuccess = () => {
          // Get existing record so we can preserve validTextShapeIds when incoming save omits it
          const getRequest = store.get(sanitizedPageId);
          getRequest.onerror = () => {
            const putRequest = store.put(dataToSave);
            putRequest.onerror = (e) => {
              console.error("❌ [INDEXED-DB] Put failed:", e.target.error?.message);
              reject(e.target.error || new Error("Put failed"));
            };
            putRequest.onsuccess = () => {
              console.warn("[INDEXED-DB] Save succeeded", sanitizedPageId);
            };
          };
          getRequest.onsuccess = () => {
            const existingResult = getRequest.result;
            preserveValidTextShapeIds(existingResult, dataToSave.data);
            const putRequest = store.put(dataToSave);
            putRequest.onerror = (e) => {
              console.error("❌ [INDEXED-DB] Put failed:", e.target.error?.message);
              reject(e.target.error || new Error("Put failed"));
            };
            putRequest.onsuccess = () => {
              console.warn("[INDEXED-DB] Save succeeded", sanitizedPageId);
            };
          };
        };

        tx.oncomplete = () => {
          db.close();
          resolve();
        };

        tx.onerror = (e) => {
          db.close();
          reject(e.target.error || new Error("Transaction failed"));
        };

        tx.onabort = (e) => {
          db.close();
          reject(e.target.error || new Error("Transaction aborted"));
        };
      };

      request.onerror = (event) => {
        clearTimeout(timeoutId);
        const error = event.target.error;

        if (error.name === "VersionError" && retries < MAX_RETRIES) {
          return openAndSave(version + 1, retries + 1)
            .then(resolve)
            .catch(reject);
        }

        reject(error);
      };

      request.onblocked = () => {
        clearTimeout(timeoutId);

        reject(new Error("DB connection blocked."));
      };
    });

  try {
    const currentVersion = await getCurrentDBVersion();
    await openAndSave(currentVersion);
  } catch (err) {
    throw err;
  }
};
